/**
 * This file is part of the "Awaken Media" project.
 *
 * (c) 2020 - CanalPlus International
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormattedPaymentMethod, FormattedOperator } from '../types';
import {
  Subtitle,
  MethodWrapper,
  MethodTitle,
  MethodInformations,
  MethodLabel,
  Operators,
  InformationsIcon
} from '../styles/components';
import OperatorWrapper from './OperatorWrapper';
import Operator from './Operator';
import ResponsiveHoc, {
  ResponsiveHocProps
} from 'shared/components/presentational/ResponsiveHOC/ResponsiveHoc';
import { sendEvent } from 'shared/helpers/tracking/sendEvent';

interface Props {
  method: FormattedPaymentMethod;
  onNonMobileOperatorClick: (operator: FormattedOperator) => void;
}

const PaymentMethod = ({
  onNonMobileOperatorClick,
  method: { title, subtitle, informations, operators, exclusiveMobile },
  mediaQueries: { toPhone }
}: Props & ResponsiveHocProps) => {
  const { label, backgroundColor, color, iconAlt, iconUrl } = informations;
  const { t } = useTranslation();

  return (
    <MethodWrapper>
      <MethodTitle>{title}</MethodTitle>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}

      {exclusiveMobile && (
        <MethodInformations backgroundColor={backgroundColor}>
          {iconUrl ? <InformationsIcon alt={iconAlt} src={iconUrl} /> : null}
          <MethodLabel color={color}>
            {label ??
              t('blocks.selfcare.subscriptionPaymentMethods.exclusiveLabel')}
          </MethodLabel>
        </MethodInformations>
      )}

      <Operators>
        {operators.map(operator => {
          const inactive = !toPhone && exclusiveMobile;
          const clickProps = exclusiveMobile
            ? {
                to: operator.link,
                onClick: () => {
                  sendEvent('renew subscription', {
                    event_name: 'renew subscription',
                    payment_method: 'USSD',
                    mobile_operator: operator.label
                  });
                }
              }
            : { onClick: () => onNonMobileOperatorClick(operator) };

          return (
            <OperatorWrapper
              key={operator.label}
              inactive={inactive}
              {...clickProps}
            >
              <Operator operator={operator} inactive={inactive} />
            </OperatorWrapper>
          );
        })}
      </Operators>
    </MethodWrapper>
  );
};

export default ResponsiveHoc(PaymentMethod);
